import React, { useState } from 'react';
import { ShoppingBag, Check, ArrowRight, FileText, Shield, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterPopup from '../components/RegisterPopup';
import ServiceProcessSection from '../components/ServiceProcessSection';
import ServiceFAQSection from '../components/ServiceFAQSection';
import SEO from '../components/SEO';

const AmazonBrandRegistry = () => {
  const [showPopup, setShowPopup] = useState(false);

  const benefits = [
    'Enhanced brand protection on Amazon',
    'Access to A+ Content for better product pages',
    'Sponsored Brands advertising access',
    'Brand Analytics for valuable insights',
    'Proactive brand protection tools',
    'Faster removal of counterfeit listings',
    'Amazon Stores for branded shopping experience',
    'Increased buy box ownership'
  ];

  const requirements = [
    'Active registered trademark',
    'Trademark appearing on products or packaging',
    'Brand website or product catalog',
    'Ability to verify trademark ownership'
  ];

  const processSteps = [
    {
      icon: 'FileText',
      title: 'Trademark Verification',
      description: 'We verify your trademark registration status and ensure it meets Amazon\'s requirements for Brand Registry enrollment.'
    },
    {
      icon: 'Shield',
      title: 'Application Preparation',
      description: 'Our team prepares all necessary documentation and information required for your Brand Registry application.'
    },
    {
      icon: 'Award',
      title: 'Enrollment & Setup',
      description: 'We guide you through enrollment and help you set up Brand Registry features to maximize protection and growth.'
    }
  ];

  const icons = {
    FileText: FileText,
    Shield: Shield,
    Award: Award
  };

  const amazonFAQs = [
    {
      question: 'Do I need a trademark to enroll in Brand Registry?',
      answer: 'Yes, you must have an active registered trademark in the country where you want to enroll. We can help you register your trademark first if you don\'t have one yet.'
    },
    {
      question: 'How long does Brand Registry enrollment take?',
      answer: 'Once you have an active trademark, the Brand Registry enrollment process typically takes 1-2 weeks. The verification process may require additional documentation or clarification from Amazon.'
    },
    {
      question: 'What benefits do I get from Brand Registry?',
      answer: 'Brand Registry provides access to A+ Content, Sponsored Brands advertising, Brand Analytics, proactive brand protection tools, and faster removal of counterfeit listings. It also helps you maintain greater control over your product listings.'
    },
    {
      question: 'Can I enroll multiple brands?',
      answer: 'Yes, you can enroll multiple brands in Brand Registry, but each brand must have its own registered trademark. Each brand enrollment is processed separately.'
    },
    {
      question: 'What if my trademark is still pending?',
      answer: 'Amazon Brand Registry requires a registered (not pending) trademark. If your trademark is still pending with the USPTO, we recommend waiting for approval before enrolling. We can help monitor your application status.'
    }
  ];

  return (
    <div>
      <SEO 
        title="Amazon Brand Registry Services - Protect Your Brand on Amazon | Trademark Flux"
        description="Get your brand registered on Amazon Brand Registry. Enhanced protection, advertising tools, and brand control. Expert assistance with enrollment and trademark requirements."
        keywords="amazon brand registry, amazon seller protection, brand registry enrollment, amazon trademark, seller brand protection"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(37, 99, 235, 0.7)), url('https://images.unsplash.com/photo-1649734926695-1b1664e98842?q=80&w=2000')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Amazon Brand Registry Services
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Protect your brand on Amazon and unlock powerful tools to grow your business. We help you navigate the entire Brand Registry process.
            </p>
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
            >
              Register Your Brand on Amazon
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* What is Amazon Brand Registry Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-aos="zoom-in" data-aos="fade-up">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Amazon Brand Registry?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Amazon Brand Registry is a program that helps rights owners protect their intellectual property and create accurate, trusted experiences for customers on Amazon. It provides powerful tools to search, report, and remove potentially infringing content.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Beyond protection, Brand Registry unlocks access to enhanced marketing features, detailed analytics, and tools to build your brand presence on Amazon's marketplace.
              </p>
            </div>
            <div className="bg-blue-600 rounded-lg p-8 text-white" data-aos="zoom-in" data-aos="fade-up">
              <ShoppingBag className="h-16 w-16 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Unlock Amazon's Power Tools</h3>
              <p className="mb-6">
                Brand Registry gives you access to exclusive features that help you protect and grow your Amazon business.
              </p>
              <Button
                onClick={() => setShowPopup(true)}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Benefits of Amazon Brand Registry
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-md" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Brand Registry Requirements
            </h2>
            <div className="bg-blue-50 rounded-lg p-8" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
              <p className="text-lg text-gray-600 mb-6">
                To enroll in Amazon Brand Registry, you must meet these essential requirements:
              </p>
              <div className="space-y-4">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-start" data-aos="zoom-in" data-aos-delay={index * 100}>
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-lg">{requirement}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-white rounded border-l-4 border-blue-600" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up">
                <p className="text-gray-700">
                  <strong>Don't have a registered trademark yet?</strong> We can help! Our trademark registration services ensure you meet all Amazon Brand Registry requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <ServiceProcessSection
        title="Our Simple 3-Step Process"
        subtitle="Get enrolled in Amazon Brand Registry in three easy steps"
        steps={processSteps}
        icons={icons}
      />

      {/* FAQ Section */}
      <ServiceFAQSection
        faqs={amazonFAQs}
        imageSrc="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=800"
        onContactClick={() => setShowPopup(true)}
      />

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Exclusive Features You'll Unlock
            </h2>
            <div className="grid md:grid-cols-3 gap-8" data-aos="fade-up" data-aos="zoom-in">
              <div className="bg-gray-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">A+ Content</h3>
                <p className="text-gray-600">
                  Create enhanced product descriptions with rich text, images, and comparison charts to increase conversions.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Brand Analytics</h3>
                <p className="text-gray-600">
                  Access valuable data about customer search terms, market basket analysis, and demographic insights.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6" data-aos="zoom-in" data-aos="fade-up" data-aos="fade-up" data-aos="fade-up">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Sponsored Brands</h3>
                <p className="text-gray-600">
                  Create custom advertising campaigns that showcase your brand logo and multiple products.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Amazon Brand?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let us help you enroll in Amazon Brand Registry and unlock powerful tools for brand protection and growth.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default AmazonBrandRegistry;